// ═══════════════════════════════════════════════════════════════════════════════
// QUICK RESPONSE BUTTON - Bouton "Aide à répondre" réutilisable
// À intégrer dans messages, commentaires, emails...
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X } from 'lucide-react';
import ResponseAssistant from './ResponseAssistant';

/**
 * Bouton compact "Aide" qui ouvre le ResponseAssistant
 * @param {string} message - Le message à analyser
 * @param {string} context - 'email' | 'message' | 'social'
 * @param {string} variant - 'icon' | 'text' | 'pill'
 * @param {string} className - Classes CSS additionnelles
 */
export function QuickResponseButton({ 
  message, 
  context = 'message',
  variant = 'pill',
  className = '',
  size = 'sm'
}) {
  const [isOpen, setIsOpen] = useState(false);
  
  const baseStyles = "transition-all flex items-center gap-1";
  
  const variantStyles = {
    icon: `p-1.5 rounded-full bg-gradient-to-r from-[#1E3A5F] to-[#3B82F6] text-white hover:shadow-md ${
      size === 'sm' ? 'text-xs' : 'text-sm'
    }`,
    text: `px-2 py-1 text-[#1E3A5F] hover:bg-[#1E3A5F]/10 rounded ${
      size === 'sm' ? 'text-xs' : 'text-sm'
    }`,
    pill: `px-2 py-0.5 bg-gradient-to-r from-[#1E3A5F] to-[#3B82F6] text-white rounded-full hover:shadow-md ${
      size === 'sm' ? 'text-[10px]' : 'text-xs'
    } font-medium`,
  };
  
  const iconSize = size === 'sm' ? 10 : 14;
  
  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(true);
        }}
        className={`${baseStyles} ${variantStyles[variant]} ${className}`}
        title="Aide à répondre"
      >
        <Sparkles size={iconSize} />
        {variant !== 'icon' && <span>Aide</span>}
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-2xl"
            >
              <ResponseAssistant 
                onClose={() => setIsOpen(false)}
                initialMessage={message}
                initialContext={context}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/**
 * Wrapper pour section commentaires avec bouton intégré
 */
export function CommentWithHelper({ 
  comment, 
  authorName,
  authorAvatar,
  time,
  onReply,
  children 
}) {
  return (
    <div className="flex gap-3 group">
      {/* Avatar */}
      <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0">
        {authorAvatar || authorName?.charAt(0)?.toUpperCase() || '?'}
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="bg-gray-100 rounded-2xl px-4 py-2">
          <p className="font-semibold text-sm text-gray-900">{authorName}</p>
          <p className="text-sm text-gray-700">{comment}</p>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
          <span>{time}</span>
          <button className="hover:text-[#1E3A5F] font-medium" onClick={onReply}>
            Répondre
          </button>
          
          {/* Bouton aide - visible au hover */}
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            <QuickResponseButton 
              message={comment} 
              context="social" 
              variant="pill"
              size="sm"
            />
          </div>
        </div>
        
        {/* Nested replies */}
        {children}
      </div>
    </div>
  );
}

export default QuickResponseButton;
